import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { FaArrowDown, FaArrowUp, FaImage, FaMobileAlt, FaSpinner } from 'react-icons/fa';
import { API_BASE, parseJsonResponse, getUserErrorMessage } from '../utils/apiBase';
import { authFetch } from '../utils/apiAuth';
import {
  formatBannerSizeError,
  isBannerSizeValid,
  readImageFileDimensions,
} from '../utils/bannerImage';

const DEFAULT_REQUIREMENTS = {
  desktop: { width: 1920, height: 450, label: '1920 × 450px' },
  mobile: { width: 1080, height: 810, label: '1080 × 810px' },
};

const getDesktopRequirements = (requirements) =>
  requirements?.desktop || (requirements?.width ? requirements : DEFAULT_REQUIREMENTS.desktop);

const getMobileRequirements = (requirements) =>
  requirements?.mobile || DEFAULT_REQUIREMENTS.mobile;

const DEFAULT_HERO = {
  enabled: true,
  autoplay: true,
  autoplaySpeed: 3000,
  slides: [],
  requirements: DEFAULT_REQUIREMENTS,
};

function UploadStatusPanel({ title, subtitle, fileName }) {
  return (
    <div style={styles.uploadPanel} role="status" aria-live="polite">
      <FaSpinner style={styles.uploadSpinner} aria-hidden="true" />
      <p style={styles.uploadTitle}>{title}</p>
      {fileName && <p style={styles.uploadFileName}>{fileName}</p>}
      {subtitle && <p style={styles.uploadSubtitle}>{subtitle}</p>}
      <p style={styles.uploadHint}>Please wait — do not close this page.</p>
    </div>
  );
}

export default function WebsiteBanner() {
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadState, setUploadState] = useState(null);
  const fileInputRef = useRef(null);
  const desktopReplaceFileInputRef = useRef(null);
  const mobileFileInputRef = useRef(null);
  const [desktopReplaceSlideId, setDesktopReplaceSlideId] = useState(null);
  const [mobileUploadSlideId, setMobileUploadSlideId] = useState(null);

  const isUploading = Boolean(uploadState);
  const isBusy = loading || saving || isUploading;

  const fetchHero = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/v1/settings/hero-banner`);
      const result = await parseJsonResponse(response);
      setHero({ ...DEFAULT_HERO, ...(result.data || {}) });
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to load home banner.'));
      setHero(DEFAULT_HERO);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHero();
  }, [fetchHero]);

  const persistHero = useCallback(async (payload, { successMessage = 'Banner saved' } = {}) => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/v1/settings/hero-banner`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await parseJsonResponse(response);
      setHero({ ...DEFAULT_HERO, ...(result.data || {}) });
      toast.success(successMessage);
      return true;
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to save banner.'));
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const updateSetting = async (key, value, message) => {
    const next = { ...hero, [key]: value };
    setHero(next);
    await persistHero(
      {
        enabled: next.enabled,
        autoplay: next.autoplay,
        autoplaySpeed: next.autoplaySpeed,
        slides: next.slides,
      },
      { successMessage: message }
    );
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Upload JPG, PNG, or WEBP only');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image must be 8MB or smaller');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const required = getDesktopRequirements(hero.requirements);
    const toastId = toast.loading('Checking desktop banner size…');

    setUploadState({ type: 'desktop', step: 'checking', fileName: file.name });

    try {
      const { width, height } = await readImageFileDimensions(file);
      if (!isBannerSizeValid(required, { width, height })) {
        toast.error(formatBannerSizeError(required, { width, height }), { id: toastId, duration: 6000 });
        setUploadState(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
    } catch {
      toast.error('Unable to read image size. Try another JPG, PNG, or WEBP file.', { id: toastId });
      setUploadState(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploadState({ type: 'desktop', step: 'uploading', fileName: file.name });
    toast.loading('Uploading desktop banner…', { id: toastId });

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await authFetch(`${API_BASE}/v1/settings/hero-banner/slides`, {
        method: 'POST',
        body: formData,
      });
      const result = await parseJsonResponse(response);
      setHero({ ...DEFAULT_HERO, ...(result.data || {}) });
      toast.success('Desktop banner uploaded successfully', { id: toastId });
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Upload failed.'), { id: toastId });
    } finally {
      setUploadState(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDesktopReplace = async (event) => {
    const file = event.target.files?.[0];
    const slideId = desktopReplaceSlideId;
    if (!file || !slideId) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Upload JPG, PNG, or WEBP only');
      if (desktopReplaceFileInputRef.current) desktopReplaceFileInputRef.current.value = '';
      setDesktopReplaceSlideId(null);
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image must be 8MB or smaller');
      if (desktopReplaceFileInputRef.current) desktopReplaceFileInputRef.current.value = '';
      setDesktopReplaceSlideId(null);
      return;
    }

    const required = getDesktopRequirements(hero.requirements);
    const toastId = toast.loading('Checking desktop banner size…');

    setUploadState({ type: 'desktop-replace', slideId, step: 'checking', fileName: file.name });

    try {
      const { width, height } = await readImageFileDimensions(file);
      if (!isBannerSizeValid(required, { width, height })) {
        toast.error(formatBannerSizeError(required, { width, height }), { id: toastId, duration: 6000 });
        setUploadState(null);
        setDesktopReplaceSlideId(null);
        if (desktopReplaceFileInputRef.current) desktopReplaceFileInputRef.current.value = '';
        return;
      }
    } catch {
      toast.error('Unable to read image size. Try another JPG, PNG, or WEBP file.', { id: toastId });
      setUploadState(null);
      setDesktopReplaceSlideId(null);
      if (desktopReplaceFileInputRef.current) desktopReplaceFileInputRef.current.value = '';
      return;
    }

    setUploadState({ type: 'desktop-replace', slideId, step: 'uploading', fileName: file.name });
    toast.loading('Replacing desktop banner…', { id: toastId });

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await authFetch(`${API_BASE}/v1/settings/hero-banner/slides/${slideId}`, {
        method: 'POST',
        body: formData,
      });
      const result = await parseJsonResponse(response);
      setHero({ ...DEFAULT_HERO, ...(result.data || {}) });
      toast.success('Desktop banner replaced successfully', { id: toastId });
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Replace failed.'), { id: toastId });
    } finally {
      setUploadState(null);
      setDesktopReplaceSlideId(null);
      if (desktopReplaceFileInputRef.current) desktopReplaceFileInputRef.current.value = '';
    }
  };

  const deleteDesktopImage = async (slideId) => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/v1/settings/hero-banner/slides/${slideId}/desktop`, {
        method: 'DELETE',
      });
      const result = await parseJsonResponse(response);
      setHero({ ...DEFAULT_HERO, ...(result.data || {}) });
      toast.success('Desktop banner removed');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to remove desktop banner.'));
      await fetchHero();
    } finally {
      setSaving(false);
    }
  };

  const handleMobileUpload = async (event) => {
    const file = event.target.files?.[0];
    const slideId = mobileUploadSlideId;
    if (!file || !slideId) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Upload JPG, PNG, or WEBP only');
      if (mobileFileInputRef.current) mobileFileInputRef.current.value = '';
      setMobileUploadSlideId(null);
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image must be 8MB or smaller');
      if (mobileFileInputRef.current) mobileFileInputRef.current.value = '';
      setMobileUploadSlideId(null);
      return;
    }

    const required = getMobileRequirements(hero.requirements);
    const toastId = toast.loading('Checking mobile banner size…');

    setUploadState({ type: 'mobile', slideId, step: 'checking', fileName: file.name });

    try {
      const { width, height } = await readImageFileDimensions(file);
      if (!isBannerSizeValid(required, { width, height })) {
        toast.error(formatBannerSizeError(required, { width, height }), { id: toastId, duration: 6000 });
        setUploadState(null);
        setMobileUploadSlideId(null);
        if (mobileFileInputRef.current) mobileFileInputRef.current.value = '';
        return;
      }
    } catch {
      toast.error('Unable to read image size. Try another JPG, PNG, or WEBP file.', { id: toastId });
      setUploadState(null);
      setMobileUploadSlideId(null);
      if (mobileFileInputRef.current) mobileFileInputRef.current.value = '';
      return;
    }

    setUploadState({ type: 'mobile', slideId, step: 'uploading', fileName: file.name });
    toast.loading('Uploading mobile banner…', { id: toastId });

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await authFetch(`${API_BASE}/v1/settings/hero-banner/slides/${slideId}/mobile`, {
        method: 'POST',
        body: formData,
      });
      const result = await parseJsonResponse(response);
      setHero({ ...DEFAULT_HERO, ...(result.data || {}) });
      toast.success('Mobile banner uploaded successfully', { id: toastId });
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Mobile upload failed.'), { id: toastId });
    } finally {
      setUploadState(null);
      setMobileUploadSlideId(null);
      if (mobileFileInputRef.current) mobileFileInputRef.current.value = '';
    }
  };

  const deleteMobileImage = async (slideId) => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/v1/settings/hero-banner/slides/${slideId}/mobile`, {
        method: 'DELETE',
      });
      const result = await parseJsonResponse(response);
      setHero({ ...DEFAULT_HERO, ...(result.data || {}) });
      toast.success('Mobile banner removed');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to remove mobile banner.'));
      await fetchHero();
    } finally {
      setSaving(false);
    }
  };

  const moveSlide = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= hero.slides.length) return;

    const slides = [...hero.slides];
    [slides[index], slides[target]] = [slides[target], slides[index]];
    setHero((prev) => ({ ...prev, slides }));
    await persistHero(
      {
        enabled: hero.enabled,
        autoplay: hero.autoplay,
        autoplaySpeed: hero.autoplaySpeed,
        slides,
      },
      { successMessage: 'Banner order saved' }
    );
  };

  const updateSlideMeta = async (index, field, value) => {
    const slides = hero.slides.map((slide, i) =>
      i === index ? { ...slide, [field]: value } : slide
    );
    setHero((prev) => ({ ...prev, slides }));
    await persistHero(
      {
        enabled: hero.enabled,
        autoplay: hero.autoplay,
        autoplaySpeed: hero.autoplaySpeed,
        slides,
      },
      { successMessage: 'Banner updated' }
    );
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes wb-upload-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <Toaster position="top-center" />

      <div style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>Home Image Banner</h1>
          <p style={styles.headerSubtitle}>Manage the large slider images on the website homepage</p>
        </div>
        {saving && !isUploading && <span style={styles.savingBadge}>Saving…</span>}
      </div>

      {uploadState?.type === 'desktop' && (
        <UploadStatusPanel
          title={uploadState.step === 'checking' ? 'Checking image size…' : 'Uploading desktop banner…'}
          subtitle={uploadState.step === 'checking' ? 'Verifying 1920 × 450px' : 'Sending image to server'}
          fileName={uploadState.fileName}
        />
      )}

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Banner Settings</h2>
        <div style={styles.settingsRow}>
          <label style={styles.checkLabel}>
            <input
              type="checkbox"
              checked={hero.enabled}
              disabled={isBusy}
              onChange={(e) => updateSetting('enabled', e.target.checked, e.target.checked ? 'Banner enabled' : 'Banner hidden')}
              style={styles.checkbox}
            />
            <span>Show banner on homepage</span>
          </label>
          <label style={styles.checkLabel}>
            <input
              type="checkbox"
              checked={hero.autoplay}
              disabled={isBusy}
              onChange={(e) => updateSetting('autoplay', e.target.checked, 'Autoplay saved')}
              style={styles.checkbox}
            />
            <span>Autoplay slides</span>
          </label>
          <label style={styles.selectLabel}>
            <span style={styles.selectLabelText}>Slide interval</span>
            <select
              value={hero.autoplaySpeed}
              disabled={isBusy}
              onChange={(e) => updateSetting('autoplaySpeed', Number(e.target.value), 'Interval saved')}
              style={styles.select}
            >
              <option value={2000}>2 seconds</option>
              <option value={3000}>3 seconds</option>
              <option value={5000}>5 seconds</option>
              <option value={8000}>8 seconds</option>
            </select>
          </label>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h2 style={styles.sectionTitle}>Banner Images</h2>
            <p style={styles.sectionDesc}>
              Upload separate images for desktop and mobile. Mobile banner is taller for phones.
            </p>
            <div style={styles.sizeRule}>
              <div><strong>Desktop:</strong> {getDesktopRequirements(hero.requirements).label}</div>
              <div style={{ marginTop: 6 }}>
                <strong>Mobile:</strong> {getMobileRequirements(hero.requirements).label}
              </div>
            </div>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleUpload}
              disabled={isBusy}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              style={{
                ...styles.uploadBtn,
                ...(uploadState?.type === 'desktop' ? styles.uploadBtnBusy : {}),
              }}
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy}
            >
              {uploadState?.type === 'desktop' ? (
                <>
                  <FaSpinner style={styles.btnSpinner} />
                  {uploadState.step === 'checking' ? 'Checking size…' : 'Uploading…'}
                </>
              ) : (
                <>
                  <FaImage style={{ marginRight: 8 }} />
                  Upload Desktop Banner
                </>
              )}
            </button>
          </div>
        </div>

        <input
          ref={desktopReplaceFileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          onChange={handleDesktopReplace}
          disabled={isBusy}
          style={{ display: 'none' }}
        />
        <input
          ref={mobileFileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          onChange={handleMobileUpload}
          disabled={isBusy}
          style={{ display: 'none' }}
        />

        {loading ? (
          <p style={styles.muted}>Loading banner images…</p>
        ) : hero.slides.length === 0 ? (
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>🖼️</span>
            <p style={styles.emptyText}>No banner images. Upload a new homepage banner.</p>
          </div>
        ) : (
          <div style={styles.slideGrid}>
            {hero.slides.map((slide, index) => {
              const isMobileUploadingHere =
                uploadState?.type === 'mobile' && uploadState.slideId === slide.id;
              const isDesktopUploadingHere =
                uploadState?.type === 'desktop-replace' && uploadState.slideId === slide.id;

              return (
              <article key={slide.id} style={styles.slideCard}>
                {(isMobileUploadingHere || isDesktopUploadingHere) && (
                  <div style={styles.slideUploadOverlay}>
                    <UploadStatusPanel
                      title={
                        isDesktopUploadingHere
                          ? uploadState.step === 'checking'
                            ? 'Checking desktop size…'
                            : 'Replacing desktop banner…'
                          : uploadState.step === 'checking'
                            ? 'Checking mobile size…'
                            : 'Uploading mobile banner…'
                      }
                      subtitle={
                        isDesktopUploadingHere
                          ? uploadState.step === 'checking'
                            ? 'Verifying 1920 × 450px'
                            : 'Sending image to server'
                          : uploadState.step === 'checking'
                            ? 'Verifying 1080 × 810px'
                            : 'Sending image to server'
                      }
                      fileName={uploadState.fileName}
                    />
                  </div>
                )}
                {slide.imageUrl ? (
                  <div style={styles.slidePreview}>
                    <img src={slide.imageUrl} alt={slide.alt || `Desktop banner ${index + 1}`} style={styles.slideImg} />
                    <span style={styles.slideBadge}>Desktop</span>
                  </div>
                ) : (
                  <div style={styles.mobileMissing}>
                    No desktop banner — upload or replace a desktop image for this slide.
                  </div>
                )}
                <div style={styles.imageActions}>
                  <button
                    type="button"
                    style={{
                      ...styles.imageActionBtn,
                      ...(isDesktopUploadingHere ? styles.uploadBtnBusy : {}),
                    }}
                    onClick={() => {
                      setDesktopReplaceSlideId(slide.id);
                      desktopReplaceFileInputRef.current?.click();
                    }}
                    disabled={isBusy}
                  >
                    {isDesktopUploadingHere ? (
                      <>
                        <FaSpinner style={styles.btnSpinner} />
                        {uploadState.step === 'checking' ? 'Checking…' : 'Uploading…'}
                      </>
                    ) : (
                      <>
                        <FaImage style={{ marginRight: 6 }} />
                        {slide.imageUrl ? 'Replace Desktop' : 'Upload Desktop'}
                      </>
                    )}
                  </button>
                  {slide.imageUrl && (
                    <button
                      type="button"
                      style={styles.imageRemoveBtn}
                      onClick={() => deleteDesktopImage(slide.id)}
                      disabled={isBusy}
                    >
                      Remove Desktop
                    </button>
                  )}
                </div>
                {slide.mobileImageUrl ? (
                  <div style={styles.mobilePreview}>
                    <img src={slide.mobileImageUrl} alt={`Mobile banner ${index + 1}`} style={styles.slideImg} />
                    <span style={styles.slideBadge}>Mobile</span>
                  </div>
                ) : (
                  <div style={styles.mobileMissing}>
                    No mobile banner — homepage banner will stay hidden on phones until you upload one.
                  </div>
                )}
                <div style={styles.slideMeta}>
                  <label style={styles.fieldLabel}>
                    Alt text
                    <input
                      style={styles.input}
                      value={slide.alt || ''}
                      onChange={(e) =>
                        setHero((prev) => ({
                          ...prev,
                          slides: prev.slides.map((s, i) => (i === index ? { ...s, alt: e.target.value } : s)),
                        }))
                      }
                      onBlur={(e) => updateSlideMeta(index, 'alt', e.target.value)}
                      placeholder="Banner description"
                    />
                  </label>
                  <label style={styles.fieldLabel}>
                    Link (optional)
                    <input
                      style={styles.input}
                      value={slide.link || ''}
                      onChange={(e) =>
                        setHero((prev) => ({
                          ...prev,
                          slides: prev.slides.map((s, i) => (i === index ? { ...s, link: e.target.value } : s)),
                        }))
                      }
                      onBlur={(e) => updateSlideMeta(index, 'link', e.target.value)}
                      placeholder="https://..."
                    />
                  </label>
                </div>
                <div style={styles.imageActions}>
                  <button
                    type="button"
                    style={{
                      ...styles.imageActionBtn,
                      ...(isMobileUploadingHere ? styles.uploadBtnBusy : {}),
                    }}
                    onClick={() => {
                      setMobileUploadSlideId(slide.id);
                      mobileFileInputRef.current?.click();
                    }}
                    disabled={isBusy}
                  >
                    {isMobileUploadingHere ? (
                      <>
                        <FaSpinner style={styles.btnSpinner} />
                        {uploadState.step === 'checking' ? 'Checking…' : 'Uploading…'}
                      </>
                    ) : (
                      <>
                        <FaMobileAlt style={{ marginRight: 6 }} />
                        {slide.mobileImageUrl ? 'Replace Mobile' : 'Upload Mobile'}
                      </>
                    )}
                  </button>
                  {slide.mobileImageUrl && (
                    <button
                      type="button"
                      style={styles.imageRemoveBtn}
                      onClick={() => deleteMobileImage(slide.id)}
                      disabled={isBusy}
                    >
                      Remove Mobile
                    </button>
                  )}
                </div>
                <div style={styles.slideActions}>
                  <button type="button" style={styles.iconBtn} onClick={() => moveSlide(index, -1)} disabled={index === 0 || isBusy} title="Move up">
                    <FaArrowUp />
                  </button>
                  <button type="button" style={styles.iconBtn} onClick={() => moveSlide(index, 1)} disabled={index === hero.slides.length - 1 || isBusy} title="Move down">
                    <FaArrowDown />
                  </button>
                </div>
              </article>
            );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    fontFamily: "'Inter', -apple-system, sans-serif",
    backgroundColor: '#E5D0A5',
    color: '#000',
    padding: '24px',
    minHeight: '100vh',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '20px',
  },
  headerTitle: {
    fontSize: '1.375em',
    margin: 0,
    fontWeight: '700',
    color: '#000',
    letterSpacing: '-0.02em',
  },
  headerSubtitle: {
    fontSize: '0.95em',
    color: '#333',
    margin: '6px 0 0 0',
  },
  savingBadge: {
    color: '#000',
    backgroundColor: '#F9D978',
    padding: '8px 14px',
    borderRadius: '8px',
    fontSize: '0.85em',
    fontWeight: '600',
  },
  section: {
    backgroundColor: '#000',
    padding: '24px',
    borderRadius: '16px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '1rem',
    margin: 0,
    color: '#F9D978',
    fontWeight: '600',
  },
  sectionDesc: {
    fontSize: '0.8em',
    color: '#888',
    margin: '6px 0 0 0',
    maxWidth: '560px',
  },
  sizeRule: {
    marginTop: '12px',
    padding: '12px 14px',
    borderRadius: '10px',
    backgroundColor: 'rgba(249, 217, 120, 0.12)',
    border: '1px solid rgba(249, 217, 120, 0.45)',
    color: '#F9D978',
    fontSize: '0.9em',
    lineHeight: 1.5,
    maxWidth: '560px',
  },
  sizeRuleNote: {
    color: '#aaa',
    fontWeight: 400,
  },
  settingsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    alignItems: 'flex-end',
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#E5D0A5',
    fontWeight: '600',
    fontSize: '0.9em',
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    accentColor: '#F9D978',
  },
  selectLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  selectLabelText: {
    color: '#888',
    fontSize: '0.85em',
    fontWeight: '500',
  },
  select: {
    backgroundColor: '#111',
    color: '#F9D978',
    border: '1px solid rgba(249, 217, 120, 0.5)',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '0.9em',
    outline: 'none',
    minWidth: '140px',
  },
  uploadBtn: {
    backgroundColor: '#F9D978',
    color: '#000',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9em',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 200,
    justifyContent: 'center',
  },
  uploadBtnBusy: {
    opacity: 0.85,
    cursor: 'wait',
  },
  uploadPanel: {
    backgroundColor: '#111',
    border: '2px solid #F9D978',
    borderRadius: '14px',
    padding: '20px 24px',
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 6,
    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
  },
  uploadSpinner: {
    fontSize: '2rem',
    color: '#F9D978',
    animation: 'wb-upload-spin 0.9s linear infinite',
    marginBottom: 4,
  },
  btnSpinner: {
    animation: 'wb-upload-spin 0.9s linear infinite',
  },
  uploadTitle: {
    margin: 0,
    color: '#F9D978',
    fontWeight: 700,
    fontSize: '1rem',
  },
  uploadFileName: {
    margin: 0,
    color: '#E5D0A5',
    fontSize: '0.85rem',
    wordBreak: 'break-all',
  },
  uploadSubtitle: {
    margin: 0,
    color: '#888',
    fontSize: '0.8rem',
  },
  uploadHint: {
    margin: '8px 0 0',
    color: '#666',
    fontSize: '0.75rem',
  },
  slideUploadOverlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 5,
    backgroundColor: 'rgba(0,0,0,0.88)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  muted: {
    color: '#888',
    margin: 0,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    color: '#666',
  },
  emptyIcon: {
    fontSize: '32px',
    marginBottom: '12px',
    opacity: 0.7,
  },
  emptyText: {
    margin: 0,
    color: '#888',
    fontSize: '0.95em',
  },
  slideGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  slideCard: {
    position: 'relative',
    backgroundColor: '#111',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #222',
  },
  slidePreview: {
    position: 'relative',
    aspectRatio: '16 / 6',
    backgroundColor: '#0a0a0a',
  },
  mobilePreview: {
    position: 'relative',
    aspectRatio: '4 / 3',
    backgroundColor: '#0a0a0a',
    borderTop: '1px solid #222',
  },
  slideBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    backgroundColor: 'rgba(0,0,0,0.75)',
    color: '#F9D978',
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '4px 8px',
    borderRadius: '6px',
  },
  mobileMissing: {
    padding: '12px',
    fontSize: '0.75rem',
    color: '#888',
    borderTop: '1px solid #222',
    lineHeight: 1.5,
  },
  mobileActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '12px',
    borderTop: '1px solid #222',
  },
  imageActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '12px',
    borderTop: '1px solid #222',
  },
  imageActionBtn: {
    backgroundColor: 'transparent',
    color: '#F9D978',
    border: '1px solid rgba(249, 217, 120, 0.5)',
    borderRadius: '8px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
  },
  imageRemoveBtn: {
    backgroundColor: 'transparent',
    color: '#f87171',
    border: '1px solid #7f1d1d',
    borderRadius: '8px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  mobileUploadBtn: {
    backgroundColor: 'transparent',
    color: '#F9D978',
    border: '1px solid rgba(249, 217, 120, 0.5)',
    borderRadius: '8px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
  },
  mobileRemoveBtn: {
    backgroundColor: 'transparent',
    color: '#f87171',
    border: '1px solid #7f1d1d',
    borderRadius: '8px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  slideImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  slideMeta: {
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  fieldLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#888',
  },
  input: {
    backgroundColor: '#0a0a0a',
    border: '1px solid #333',
    color: '#E5D0A5',
    borderRadius: '8px',
    padding: '10px 12px',
    fontSize: '0.9em',
    outline: 'none',
  },
  slideActions: {
    display: 'flex',
    gap: '8px',
    padding: '0 12px 12px',
  },
  iconBtn: {
    backgroundColor: 'transparent',
    color: '#F9D978',
    border: '1px solid #444',
    borderRadius: '8px',
    padding: '8px 12px',
    cursor: 'pointer',
  },
  deleteBtn: {
    backgroundColor: 'transparent',
    color: '#f87171',
    border: '1px solid #7f1d1d',
    borderRadius: '8px',
    padding: '8px 12px',
    cursor: 'pointer',
    marginLeft: 'auto',
  },
};
